import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import { ApiError } from '../api/client';
import { mikrotikApi } from '../api/endpoints';
import { useToast } from '../app/ToastProvider';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { TableSkeleton } from '../components/TableSkeleton';
import type { MikrotikActiveSession, MikrotikPppoeInterface } from '../api/types';
import { formatNumber } from '../utils/format';

function byteCount(value?: string): string {
  const bytes = Number(value ?? 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function MikrotikPage() {
  const [search, setSearch] = useState('');
  const toast = useToast();
  const queryClient = useQueryClient();

  const resourceQuery = useQuery({
    queryKey: ['mikrotik-resource'],
    queryFn: mikrotikApi.resource,
    refetchInterval: 60_000,
  });

  const interfacesQuery = useQuery({
    queryKey: ['mikrotik-interfaces', search],
    queryFn: () => mikrotikApi.interfaces(search),
  });

  const activeQuery = useQuery({
    queryKey: ['mikrotik-active', search],
    queryFn: () => mikrotikApi.active(search),
    refetchInterval: 30_000,
  });

  const refreshAll = async (): Promise<void> => {
    await Promise.all([resourceQuery.refetch(), interfacesQuery.refetch(), activeQuery.refetch()]);
  };

  const disconnectSession = useMutation({
    mutationFn: (session: MikrotikActiveSession) => mikrotikApi.disconnectSession(session['.id']),
    onSuccess: async ({ message }) => {
      toast.success(message);
      await queryClient.invalidateQueries({ queryKey: ['mikrotik-active'] });
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Active session could not be disconnected.');
    },
  });

  const interfaceStatus = useMutation({
    mutationFn: (item: MikrotikPppoeInterface) =>
      item.disabled === 'true'
        ? mikrotikApi.enableInterface(item['.id'])
        : mikrotikApi.disableInterface(item['.id']),
    onSuccess: async ({ message }) => {
      toast.success(message);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mikrotik-interfaces'] }),
        queryClient.invalidateQueries({ queryKey: ['mikrotik-active'] }),
      ]);
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Interface status could not be changed.');
    },
  });

  const resource = resourceQuery.data;
  const interfaces: MikrotikPppoeInterface[] = interfacesQuery.data ?? [];
  const activeSessions = activeQuery.data ?? [];

  return (
    <>
      <PageHeader
        title="MikroTik"
        description="RouterOS controls for PPPoE subscribers and live sessions."
        actions={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void refreshAll()}
            disabled={resourceQuery.isFetching || interfacesQuery.isFetching || activeQuery.isFetching}
          >
            Refresh
          </Button>
        }
      />

      {resourceQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {resourceQuery.error instanceof Error
            ? resourceQuery.error.message
            : 'MikroTik connection failed.'}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <RouterOutlinedIcon color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Router
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {resource?.['board-name'] ?? '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    RouterOS
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {resource?.version ?? '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    CPU Load
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {resource?.['cpu-load'] ? `${resource['cpu-load']}%` : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Free Memory
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {byteCount(resource?.['free-memory'])}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader
              title="PPPoE Interfaces"
              subheader={`${formatNumber(interfaces.length)} RouterOS PPP interface(s)`}
              titleTypographyProps={{ variant: 'h6' }}
              action={<TextField label="Search Interfaces" value={search} onChange={(event) => setSearch(event.target.value)} size="small" />}
            />
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Interface</TableCell><TableCell>User</TableCell><TableCell>Service</TableCell>
                  <TableCell>Remote Address</TableCell><TableCell>Local Address</TableCell><TableCell>MTU</TableCell>
                  <TableCell>Status</TableCell><TableCell>Last Link Up</TableCell><TableCell>Link Downs</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {interfacesQuery.isPending ? <TableSkeleton columns={10} /> : interfaces.length > 0 ? interfaces.map((item) => (
                    <TableRow key={item['.id']} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.name ?? '-'}</TableCell>
                      <TableCell>{item.user ?? '-'}</TableCell><TableCell>{item.service ?? '-'}</TableCell>
                      <TableCell>{item.remoteAddress ?? '-'}</TableCell><TableCell>{item['local-address'] ?? '-'}</TableCell>
                      <TableCell>{item['actual-mtu'] ?? '-'}</TableCell>
                      <TableCell><StatusBadge status={item.disabled === 'true' ? 'INACTIVE' : 'ACTIVE'} /></TableCell>
                      <TableCell>{item['last-link-up-time'] ?? '-'}</TableCell><TableCell>{item['link-downs'] ?? '0'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color={item.disabled === 'true' ? 'success' : 'warning'}
                          disabled={interfaceStatus.isPending || item.dynamic === 'true'}
                          onClick={() => interfaceStatus.mutate(item)}
                        >
                          {item.disabled === 'true' ? 'Enable' : 'Disable'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={10} sx={{ border: 0 }}><EmptyState message="No PPPoE interfaces found." /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
        <Grid size={12}>
          <Card>
            <CardHeader
              title="Active Sessions"
              subheader={`${formatNumber(activeSessions.length)} connected PPPoE session(s)`}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Uptime</TableCell>
                    <TableCell>Caller ID</TableCell>
                    <TableCell>Encoding</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeQuery.isPending ? (
                    <TableSkeleton columns={7} />
                  ) : activeSessions.length > 0 ? (
                    activeSessions.map((session) => (
                      <TableRow key={session['.id']} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{session.name ?? '-'}</TableCell>
                        <TableCell>{session.address ?? '-'}</TableCell>
                        <TableCell>{session.uptime ?? '-'}</TableCell>
                        <TableCell>{session['caller-id'] ?? '-'}</TableCell>
                        <TableCell>{session.encoding ?? '-'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<CableOutlinedIcon />}
                            disabled={disconnectSession.isPending}
                            onClick={() => disconnectSession.mutate(session)}
                          >
                            Disconnect
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ border: 0 }}>
                        <EmptyState message="No active PPPoE sessions found." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}







